/**
 * WorkerMyJobs.js - Worker's assigned jobs view
 * Shows Upcoming, Active, and Completed jobs with tabs
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, Clock, MapPin, DollarSign, ChevronRight,
  CheckCircle, PlayCircle, AlertCircle, MoreVertical,
  Navigation, Phone, MessageSquare, RefreshCw
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { jobsAPI } from '../../services/api';
import ServiceWorkflowRouter from './ServiceWorkflowRouter';

const TABS = [
  { id: 'upcoming', label: 'Upcoming', icon: Calendar },
  { id: 'active', label: 'Active', icon: PlayCircle },
  { id: 'completed', label: 'Completed', icon: CheckCircle }
];

const serviceColors = {
  cleaning: 'bg-blue-500',
  laundry: 'bg-purple-500',
  courier: 'bg-orange-500',
  rides: 'bg-green-500',
  errands: 'bg-pink-500',
  tasks: 'bg-pink-500'
};

const WorkerMyJobs = ({ onStartJob }) => {
  const [activeTab, setActiveTab] = useState('upcoming');
  const [jobs, setJobs] = useState({ upcoming: [], active: [], completed: [] });
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showWorkflow, setShowWorkflow] = useState(false);
  
  const fetchMyJobs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await jobsAPI.getMyAssignments();
      // Backend returns { assignments: [...], weekly_hours: {...} }
      const allJobs = response.data?.assignments || response.data?.jobs || [];
      
      // Categorize jobs
      const categorized = {
        upcoming: [],
        active: [],
        completed: []
      };
      
      allJobs.forEach(job => {
        const status = job.status?.toLowerCase();
        
        if (['completed', 'completion_submitted', 'paid'].includes(status)) {
          categorized.completed.push(job);
        } else if (['in_progress', 'started', 'en_route', 'arrived', 'working'].includes(status)) {
          categorized.active.push(job);
        } else if (['assigned', 'confirmed', 'pending_approval', 'accepted', 'claimed'].includes(status)) {
          categorized.upcoming.push(job);
        }
      });
      
      // Sort upcoming by date
      categorized.upcoming.sort((a, b) => 
        new Date(a.scheduled_date) - new Date(b.scheduled_date)
      );
      
      // Sort completed by date (most recent first)
      categorized.completed.sort((a, b) => 
        new Date(b.completed_at || b.scheduled_date) - new Date(a.completed_at || a.scheduled_date)
      );
      
      setJobs(categorized);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      toast.error('Failed to load your jobs');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchMyJobs();
  }, [fetchMyJobs]);
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };
  
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };
  
  const handleStartJob = (job) => {
    setSelectedJob(job);
    setShowWorkflow(true);
    onStartJob?.(job);
  };
  
  const handleWorkflowComplete = async () => {
    setShowWorkflow(false);
    setSelectedJob(null);
    await fetchMyJobs();
    toast.success('Job completed!');
  };
  
  const handleWorkflowClose = () => {
    setShowWorkflow(false);
    setSelectedJob(null);
    fetchMyJobs(); // Refresh to get updated status
  };
  
  const currentJobs = jobs[activeTab] || [];
  
  const JobCard = ({ job, showActions = true }) => {
    const serviceColor = serviceColors[job.service_type] || 'bg-gray-500';
    const workerPay = job.worker_pay || job.display_pay || 0;
    const isActive = activeTab === 'active';
    const isCompleted = activeTab === 'completed';
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl overflow-hidden"
        data-testid={`my-job-${job.id}`}
      >
        {/* Service Type Badge */}
        <div className={`${serviceColor} px-4 py-2 flex items-center justify-between`}>
          <span className="text-white font-semibold uppercase text-sm">
            {job.service_type?.replace('_', ' ')}
          </span>
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
            isCompleted 
              ? 'bg-white/20 text-white'
              : isActive
                ? 'bg-yellow-400 text-black'
                : 'bg-white/30 text-white'
          }`}>
            {job.status?.toUpperCase().replace('_', ' ')}
          </span>
        </div>
        
        <div className="p-4">
          {/* Date & Time */}
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-[var(--text-muted)]" />
            <span className="text-[var(--text-primary)] font-medium">
              {formatDate(job.scheduled_date)}
            </span>
            <span className="text-[var(--text-muted)]">•</span>
            <Clock className="w-4 h-4 text-[var(--text-muted)]" />
            <span className="text-[var(--text-secondary)]">
              {formatTime(job.scheduled_time)}
            </span>
          </div>
          
          {/* Location */}
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-4 h-4 text-[var(--text-muted)]" />
            <span className="text-[var(--text-secondary)] text-sm truncate">
              {job.address ? `${job.address}, ` : ''}{job.city}, {job.state || 'NC'}
            </span>
          </div>
          
          {/* Customer (if assigned) */}
          {(job.client_name || job.customer_name) && (
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-full bg-[var(--background-subtle)] flex items-center justify-center">
                <span className="text-xs font-medium text-[var(--text-primary)]">
                  {(job.client_name || job.customer_name)?.charAt(0).toUpperCase()}
                </span>
              </div>
              <span className="text-[var(--text-secondary)] text-sm">
                {job.client_name || job.customer_name}
              </span>
            </div>
          )}
          
          {/* Pay & Duration */}
          <div className="flex items-center justify-between pt-2 border-t border-[var(--border-primary)]">
            <div className="flex items-center gap-1">
              <DollarSign className="w-4 h-4 text-[var(--primary)]" />
              <span className="font-bold text-[var(--primary)]">
                ${workerPay.toFixed(2)}
              </span>
            </div>
            {job.duration_hours && (
              <span className="text-sm text-[var(--text-muted)]">
                {job.duration_hours} hr{job.duration_hours > 1 ? 's' : ''}
              </span>
            )}
          </div>
          
          {/* Actions */}
          {showActions && !isCompleted && (
            <div className="mt-3 flex gap-2">
              {isActive ? (
                <Button
                  onClick={() => handleStartJob(job)}
                  className="flex-1 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-black"
                  data-testid={`continue-job-${job.id}`}
                >
                  Continue Job
                </Button>
              ) : (
                <>
                  <Button
                    onClick={() => handleStartJob(job)}
                    className="flex-1 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-black"
                    data-testid={`start-job-${job.id}`}
                  >
                    Start Job
                  </Button>
                  <Button
                    variant="outline"
                    className="border-[var(--border-primary)]"
                    onClick={() => {
                      // Navigate to job location
                      if (job.address && job.city) {
                        window.open(
                          `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${job.address}, ${job.city}, ${job.state || 'NC'}`)}`,
                          '_blank'
                        );
                      }
                    }}
                  >
                    <Navigation className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
          )}
          
          {/* Completed Job - Show earnings */}
          {isCompleted && (
            <div className="mt-3 pt-3 border-t border-[var(--border-primary)]">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[var(--text-muted)]">Earned</span>
                <span className="font-bold text-green-500">+${workerPay.toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    );
  };
  
  return (
    <div className="min-h-screen bg-[var(--background)]">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-[var(--background-paper)] border-b border-[var(--border-primary)]">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-[var(--text-primary)]">My Jobs</h1>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchMyJobs}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          
          {/* Tabs */}
          <div className="flex border-b border-[var(--border-primary)]">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const count = jobs[tab.id]?.length || 0;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium border-b-2 transition-colors ${
                    isActive
                      ? 'border-[var(--primary)] text-[var(--primary)]'
                      : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  {count > 0 && (
                    <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                      isActive ? 'bg-[var(--primary)] text-black' : 'bg-[var(--background-subtle)] text-[var(--text-muted)]'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4 pb-24">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-10 h-10 border-3 border-[var(--primary)] border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-[var(--text-muted)]">Loading your jobs...</p>
          </div>
        ) : currentJobs.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-[var(--background-subtle)] rounded-full flex items-center justify-center">
              {activeTab === 'upcoming' && <Calendar className="w-8 h-8 text-[var(--text-muted)]" />}
              {activeTab === 'active' && <PlayCircle className="w-8 h-8 text-[var(--text-muted)]" />}
              {activeTab === 'completed' && <CheckCircle className="w-8 h-8 text-[var(--text-muted)]" />}
            </div>
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
              {activeTab === 'upcoming' && 'No Upcoming Jobs'}
              {activeTab === 'active' && 'No Active Jobs'}
              {activeTab === 'completed' && 'No Completed Jobs Yet'}
            </h3>
            <p className="text-[var(--text-muted)]">
              {activeTab === 'upcoming' && 'Pick up jobs from the Available Jobs feed!'}
              {activeTab === 'active' && "Start a job from your upcoming jobs to see it here."}
              {activeTab === 'completed' && 'Complete jobs to build your work history.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence>
              {currentJobs.map(job => (
                <JobCard key={job.id} job={job} />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
      
      {/* Service Workflow Modal */}
      {showWorkflow && selectedJob && (
        <ServiceWorkflowRouter
          job={selectedJob}
          isOpen={showWorkflow}
          onClose={handleWorkflowClose}
          onWorkflowComplete={handleWorkflowComplete}
        />
      )}
    </div>
  );
};

export default WorkerMyJobs;
